import React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import type { EventType } from './ClientServerGraph.types';

interface Props {
  colorModeOverride?: 'light' | 'dark';
  eventType: EventType;
  track: 'server' | 'client';
  startAt: number;
  duration: number;
  size?: 'normal' | 'small';
  angle?: number;
  opacity?: number;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

function Block({
  colorModeOverride,
  eventType,
  track,
  startAt,
  duration,
  size = 'normal',
  angle = 0,
  opacity = 1,
  style = {},
  children,
  ...delegated
}: Props) {
  const gridRow = track === 'server' ? 2 : 4;
  const gridCol = `${startAt + 1} / ${startAt + 1 + duration}`;

  return (
    <Wrapper
      layout
      data-color-mode-override={colorModeOverride}
      data-event-type={eventType}
      style={{
        ...style,
        opacity,
        gridColumn: gridCol,
        gridRow,
      }}
      {...delegated}
    >
      <TextWrapper layout data-size={size}>
        <TextRotator
          style={{
            transform: angle ? `rotate(${angle}deg)` : undefined,
          }}
        >
          {children}
        </TextRotator>
      </TextWrapper>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  align-self: center;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 65px;
  border: 2px solid var(--border-color);
  border-radius: 4px;
  background: var(--background);
  white-space: pre-wrap;
  text-align: center;
  /* font-weight: var(--font-weight-medium); */
  line-height: 1.2;
  color: var(--color-text);
  transition: opacity 250ms;
  cursor: default;
  animation: fadeIn 400ms backwards;
  will-change: transform;
  transition:
    background var(--color-swap-duration),
    border-color var(--color-swap-duration);

  /* Light Mode colors */
  &[data-event-type='render'] {
    --background: hsl(345deg 85% 90%);
    --border-color: hsl(345deg 35% 65%);
  }
  &[data-event-type='javascript'] {
    --background: hsl(50deg 100% 90%);
    --border-color: hsl(50deg 95% 40%);
  }
  &[data-event-type='hydrate'] {
    --background: hsl(200deg 83% 90%);
    --border-color: hsl(200deg 35% 65%);
  }
  &[data-event-type='data'] {
    --background: hsl(260deg 15% 88%);
    --border-color: hsl(265deg 10% 60%);
  }
  &[data-event-type='html'] {
    --background: hsl(120deg 15% 88%);
    --border-color: hsl(120deg 10% 60%);
  }

  /* Dark Mode colors */
  html[data-color-mode='dark']
    &:not([data-color-mode-override='light']) {
    &[data-event-type='render'] {
      --background: hsl(345deg 85% 10%);
      --border-color: hsl(345deg 35% 35%);
    }
    &[data-event-type='javascript'] {
      --background: hsl(50deg 100% 10%);
      --border-color: hsl(50deg 95% 30%);
    }
    &[data-event-type='hydrate'] {
      --background: hsl(200deg 83% 10%);
      --border-color: hsl(200deg 35% 35%);
    }
    &[data-event-type='data'] {
      --background: hsl(260deg 15% 12%);
      --border-color: hsl(265deg 10% 40%);
    }
    &[data-event-type='html'] {
      --background: hsl(120deg 15% 12%);
      --border-color: hsl(120deg 10% 40%);
    }
  }
`;

const TextWrapper = styled(motion.div)`
  font-size: 0.875rem;
  will-change: transform;
  line-height: inherit;

  &[data-size='small'] {
    font-size: 0.75rem;
  }
`;

const TextRotator = styled.div`
  line-height: inherit;
  transition: transform 400ms;
`;

export default Block;
